import {
  TEXT_CHANGE,
  SET_CASHFLOW,
  SET_RESUME,
  SET_SINGLE_ESTABLISHMENT_CASHFLOW,
  CLEAR_CASHFLOW,
  CLEAR_RESUME,
  HANDLE_LOADING,
  HANDLE_RESUME_LOADING,
  CHANGE_PERIOD_TYPE,
  SET_CURRENT_YEAR_IN_DAY_TYPE,
  SET_ALL_ESTABLISHMENTS_REPORT_LIST,
  SET_ALL_ESTABLISHMENT_CURRENT_REPORT_ID,
  NEXT_POINT,
  PREV_POINT,
  SET_YEAR_RANGE,
  BUILD_RESUME,
  CLEAR_BUILD_RESUME_FILTERS,
  CHANGE_DATE,
  CLEAR_DATE
} from './constants';

import moment from 'moment';
import { compose, lensProp, set, assoc, over, map } from 'ramda';
import { convertToReal } from 'helpers/converters';
import { formatDate, formatDateMonthName } from '../../../helpers/formatters';

const increase = (point, range, withLimit = true) => {
  let newPoint = parseInt(point, 10);

  const conditional = withLimit ? newPoint !== range[range.length - 1] : true;

  if(conditional) {
    newPoint += 1;
  }

  return newPoint;
}

const decrease = point => {
  let newPoint = parseInt(point, 10);
  newPoint -= 1;
  return newPoint;
}

const increaseMonth = (year, point, range, withLimit = true) => {
  let newYear = parseInt(year, 10);
  let newPoint = parseInt(point, 10);

  const conditional = withLimit ? newPoint !== range[range.length - 1] : true;

  if(conditional) {
    newPoint += 1;
  } else {
    newYear += 1;
    newPoint = 1;
  }

  return {
    year: newYear,
    month: newPoint
  }
}

const decreaseMonth = (year, point, range) => {
  let newYear = parseInt(year, 10);
  let newPoint = parseInt(point, 10);

  if(newPoint !== range[0]) {
    newPoint -= 1;
  } else {
    newYear -= 1;
    newPoint = 12;
  }

  return {
    year: newYear,
    month: newPoint
  }
}

const defaultState = {
  allEstablishmentToastId: 'multi-establishment-report-toast',
  allEstablishmentCurrentReportId: '',
  allEstablishmentsReportList: [],
  singleEstablishmentContent: [],
  singleEstablishmentResume: {
    receipts: 0,
    payments: 0,
    balance: 0
  },
  content: [],
  resume: {
    receipts: 0,
    payments: 0,
    balance: 0
  },
  isLoading: false,
  isResumeLoading: false,
  type: 'day',
  month: moment(new Date()).format('MM'),
  monthRange: [1, 12],
  year: moment(new Date()).format('YYYY'),
  yearRange: [2017, 2020],
  buildingResume: false,
  resumeTypeId: null,
  resumeMonthId: null,
  startDate: moment().format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
  startTime: '',
  endTime: ''
}

export default (state = defaultState, action) => {
  switch (action.type) {
    case TEXT_CHANGE: {
      const { value } = action;

      return set(lensProp(action.field), value, state);
    }
    case SET_CASHFLOW: {
      const { content } = action || {};

      const {
        date,
        entry,
        exit,
        balance,
        establishmentId,
        isDay,
        isMonth
      } = content || {};

      return {
        ...state,
        content: [
          ...state.content,
          {
            entry,
            exit,
            balance,
            establishmentId,
            isDay,
            isMonth,
            day: moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY'),
            dayId: moment(date, 'YYYY-MM-DD').format('D'),
            month: `${moment(date, 'YYYY-MM').format('MMMM').charAt(0).toUpperCase()}${moment(date, 'YYYY-MM').format('MMMM').slice(1)}`,
            monthId: moment(date, 'YYYY-MM').month(),
            amountReceipt: convertToReal(entry),
            amountPayment: convertToReal(exit),
            amountBalance: convertToReal(balance)
          }
        ]
      }
    }
    case SET_RESUME: {
      const { resume } = action;

      const { revenue, expense } = resume;

      return {
        ...state,
        resume: {
          receipts: revenue,
          payments: expense,
          balance: revenue - expense
        }
      }
    }
    case SET_SINGLE_ESTABLISHMENT_CASHFLOW: {
      const { resume, content } = action;

      const formatter = item => compose(
        assoc('monthId', item.month),
        over(lensProp('day'), formatDate),
        over(lensProp('month'), formatDateMonthName),
        over(lensProp('previousBalance'), convertToReal),
        over(lensProp('amountReceipt'), convertToReal),
        over(lensProp('amountPayment'), convertToReal),
        over(lensProp('amountBalance'), convertToReal),
        over(lensProp('balance'), convertToReal)
      )(item);

      const cashflow = map(formatter, content);

      return {
        ...state,
        singleEstablishmentResume: resume,
        singleEstablishmentContent: cashflow
      }
    }
    case CLEAR_CASHFLOW: {
      return {
        ...state,
        content: [],
        resume: {
          ...defaultState.resume
        }
      }
    }
    case CLEAR_RESUME: {
      return {
        ...state,
        resume: {
          ...defaultState.resume
        }
      }
    }
    case HANDLE_LOADING: {
      return {
        ...state,
        isLoading: action.status
      }
    }
    case HANDLE_RESUME_LOADING: {
      return {
        ...state,
        isResumeLoading: action.status
      }
    }
    case CHANGE_PERIOD_TYPE: {
      return {
        ...state,
        type: action.periodType
      }
    }
    case SET_CURRENT_YEAR_IN_DAY_TYPE: {
      const { year, type } = state;

      const dateElement =
        type === 'day' ? { year: moment(new Date()).format('YYYY') } : { year };

      return {
        ...state,
        ...dateElement
      }
    }
    case SET_YEAR_RANGE: {
      return {
        ...state,
        yearRange: action.yearRange
      }
    }
    case SET_ALL_ESTABLISHMENTS_REPORT_LIST: {
      return {
        ...state,
        allEstablishmentsReportList: action?.data?.map(item => {
          return {
            reportId: item?.id,
            reportTypeId: item?.reportTypeId,
            status: item?.status,
            description: item?.description,
            createdAt: item?.createdAt
          }
        })
      }
    }
    case SET_ALL_ESTABLISHMENT_CURRENT_REPORT_ID: {
      return {
        ...state,
        allEstablishmentCurrentReportId: action.id
      }
    }
    case NEXT_POINT: {
      const { year, month, type, monthRange, yearRange } = state;

      const dateElement = type == 'day'
        ? increaseMonth(year, month, monthRange)
        : { year: increase(year, yearRange, false) }

      return {
        ...state,
        ...dateElement
      }
    }
    case PREV_POINT: {
      const { year, month, type, monthRange, yearRange } = state;

      const dateElement = type == 'day'
        ? decreaseMonth(year, month, monthRange)
        : { year: decrease(year, yearRange) };

      return {
        ...state,
        ...dateElement
      }
    }
    case BUILD_RESUME: {
      return set(lensProp('buildingResume'), action.status, state);
    }
    case CLEAR_BUILD_RESUME_FILTERS: {
      return compose(
        set(lensProp('registration'), false),
        set(lensProp('resumeTypeId'), null),
        set(lensProp('resumeMonthId'), null),
        set(lensProp('startDate'), moment().format('YYYY-MM-DD')),
        set(lensProp('endDate'), moment().format('YYYY-MM-DD'))
      )(state);
    }
    case CHANGE_DATE: {
      const convertTimeToBD = time => moment(time).format('YYYY-MM-DD');

      return {
        ...state,
        startDate: convertTimeToBD(action.startDate),
        endDate: convertTimeToBD(action.endDate)
      }
    }
    case CLEAR_DATE:
      return defaultState;
    default:
      return state;
  }
}